<script setup>
import { computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const formatDate = dateString => {
  return format(new Date(dateString), 'MMM d, yyyy');
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
};

const getStatusClass = status => {
  const classes = {
    completed: 'bg-n-teal-5 text-n-teal-12',
    processing: 'bg-n-blue-5 text-n-blue-12',
    'on-hold': 'bg-n-amber-5 text-n-amber-12',
    pending: 'bg-n-amber-5 text-n-amber-12',
    cancelled: 'bg-n-ruby-5 text-n-ruby-12',
    refunded: 'bg-n-slate-5 text-n-slate-12',
    failed: 'bg-n-ruby-5 text-n-ruby-12',
  };
  return classes[status] || 'bg-n-solid-3 text-n-slate-12';
};

const orderStatus = computed(() => {
  const { status } = props.order;
  if (!status) {
    return '';
  }
  const key = `CONVERSATION_SIDEBAR.WOOCOMMERCE.STATUS.${status.toUpperCase().replace('-', '_')}`;
  return t(key);
});
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
  >
    <div class="flex justify-between items-center">
      <div class="font-medium flex">
        <a
          :href="order.admin_url"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline text-n-slate-12 cursor-pointer truncate"
        >
          {{
            $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.ORDER_ID', { id: order.id })
          }}
          <i class="i-lucide-external-link pl-5" />
        </a>
      </div>
      <div
        :class="getStatusClass(order.status)"
        class="text-xs px-2 py-1 rounded capitalize truncate"
        :title="orderStatus"
      >
        {{ orderStatus }}
      </div>
    </div>
    <div class="text-sm text-n-slate-12">
      <span class="text-n-slate-11 border-r border-n-weak pr-2">
        {{ formatDate(order.date_created) }}
      </span>
      <span class="text-n-slate-11 pl-2">
        {{ formatCurrency(order.total, order.currency) }}
      </span>
    </div>
  </div>
</template>
